import { useCallback, useEffect, useRef } from 'react';

/**
 * Runs async work with an `AbortSignal` that fires when the component
 * unmounts.
 *
 * Needed because React Query's `useMutation` — unlike `useQuery` — hands the
 * mutation function no signal, so long-running work started from a mutation
 * has nothing tying it to the component's lifetime. The Explore chat's
 * recovery poll is the motivating case: it can keep polling for minutes after
 * the user has navigated away.
 *
 * A controller is created per call and dropped when that call settles, so a
 * long-lived component does not accumulate one per message.
 */
export function useAbortOnUnmount(): <T>(
  run: (signal: AbortSignal) => Promise<T>
) => Promise<T> {
  const controllersRef = useRef<Set<AbortController>>(new Set());

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  return useCallback(<T,>(run: (signal: AbortSignal) => Promise<T>): Promise<T> => {
    const controller = new AbortController();
    controllersRef.current.add(controller);
    return run(controller.signal).finally(() => {
      controllersRef.current.delete(controller);
    });
  }, []);
}
